import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import {
  type FilterDefinition,
  FiltersProvider,
  useFilters,
} from '@spotify/confidence-core-react';
import { AdminLabelsV1LabelResourceType } from '@spotify/confidence-graphql';

import { LabelDefinition, useLabelsForResource } from '../../hooks';
import { createLabelFilters } from './LabelFilter';

type LabelFiltersContextValue = {
  labelDefinitions: LabelDefinition[];
  addLabelFilter: (labelKey: string, value: string) => void;
};

const LabelFiltersContext = createContext<LabelFiltersContextValue>({
  labelDefinitions: [],
  addLabelFilter: () => {},
});

type FiltersProviderWithLabelsProps = {
  filters?: FilterDefinition[];
  storageKey: string;
  children: ReactNode;
  searchField: string;
  defaultFilters?: Array<{ id: string; value: unknown }>;
  resourceType: AdminLabelsV1LabelResourceType;
  disableQueryParams?: boolean;
};

export function FiltersProviderWithLabels({
  filters = [],
  storageKey,
  children,
  searchField,
  defaultFilters = [],
  resourceType,
  disableQueryParams,
}: FiltersProviderWithLabelsProps) {
  const { labelDefinitions } = useLabelsForResource({ resourceType });

  const labelFilters = useMemo(
    () => createLabelFilters(labelDefinitions),
    [labelDefinitions],
  );

  const allFilters = useMemo(
    () => [...filters, ...labelFilters],
    [filters, labelFilters],
  );

  return (
    <FiltersProvider
      filters={allFilters}
      storageKey={storageKey}
      searchField={searchField}
      defaultFilters={defaultFilters}
      disableQueryParams={disableQueryParams}
    >
      <LabelFiltersContextProvider labelDefinitions={labelDefinitions}>
        {children}
      </LabelFiltersContextProvider>
    </FiltersProvider>
  );
}

function LabelFiltersContextProvider({
  labelDefinitions,
  children,
}: {
  labelDefinitions: LabelDefinition[];
  children: ReactNode;
}) {
  const { addFilter } = useFilters();

  const labelFiltersMap = useMemo(() => {
    const map = new Map<
      string,
      ReturnType<typeof createLabelFilters>[number]
    >();
    for (const def of labelDefinitions) {
      map.set(def.id, createLabelFilters([def])[0]);
    }
    return map;
  }, [labelDefinitions]);

  const addLabelFilter = useCallback(
    (labelKey: string, value: string) => {
      const labelFilter = labelFiltersMap.get(labelKey);
      if (labelFilter) {
        addFilter(labelFilter, [value]);
      }
    },
    [labelFiltersMap, addFilter],
  );

  const contextValue = useMemo(
    () => ({
      labelDefinitions,
      addLabelFilter,
    }),
    [labelDefinitions, addLabelFilter],
  );

  return (
    <LabelFiltersContext.Provider value={contextValue}>
      {children}
    </LabelFiltersContext.Provider>
  );
}

export function useLabelFilters() {
  return useContext(LabelFiltersContext);
}
